import Icon1 from "@assets/icons/Icon";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { EmailTemplateModal } from "@components/modals/EmailTemplateModal";
import ConfirmationModal from "@components/support/ConfirmationModal";
import SearchField from "@components/ui/textfields/SearchField";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { getLocalFromFlutter } from "@services/timeHelpers";
import { useProjectStore } from "@store/projectStore";
import { useDataStore } from "@store/user_org";
import { useState, useRef, useCallback } from "react";
import { emailTemplatesApi } from "@services/api";
import { showNotification } from "context/notification.service";
import { defaultEmailTemplates } from "./defaultEmailTemplates";
import { colors } from "sexy";
import CheckBox from "@components/ui/selection/CheckBox";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import type { EmailTemplate } from "types/email_template";

const ITEM_TYPE = "EMAIL_TEMPLATE_ROW";

interface DragItem {
  index: number;
  id: string;
}

const DraggableTemplateRow = ({
  template,
  index,
  isSelected,
  onToggleSelect,
  onSelect,
  moveRow,
  onDropEnd,
}: {
  template: EmailTemplate;
  index: number;
  isSelected: boolean;
  onToggleSelect: () => void;
  onSelect: () => void;
  moveRow: (dragIndex: number, hoverIndex: number) => void;
  onDropEnd: () => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const docCount = template.library_documents_id?.length || 0;

  const [{ isDragging }, drag, preview] = useDrag({
    type: ITEM_TYPE,
    item: (): DragItem => ({ index, id: template.id }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: () => {
      onDropEnd();
    },
  });

  const [{ isOver }, drop] = useDrop<DragItem, void, { isOver: boolean }>({
    accept: ITEM_TYPE,
    hover: (item, monitor) => {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveRow(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const dragHandleRef = useRef<HTMLDivElement>(null);
  preview(drop(ref));
  drag(dragHandleRef);

  return (
    <div
      ref={ref}
      className={`flex flex-row rounded-xl items-center px-4 py-2 transition-all ${
        isSelected ? "bg-neutral-50" : ""
      } ${isDragging ? "opacity-40" : ""} ${
        isOver ? "border-t-2 border-blue-400" : ""
      }`}
      style={{ cursor: "default" }}
    >
      <div
        ref={dragHandleRef}
        className="flex items-center justify-center mr-2 cursor-grab active:cursor-grabbing text-neutral-400 hover:text-neutral-600 select-none"
        style={{ fontSize: 18, lineHeight: 1, padding: "4px 2px" }}
        title="Glisser pour réordonner"
      >
        ⠿
      </div>
      <CheckBox checked={isSelected} onChange={onToggleSelect} />
      <div className="flex flex-col w-250 ml-2">
        <div className="text-h2 font-medium">
          {template.destination_type === "partner" && (
            <span className="text-h4 text-orange-500 bg-orange-50 px-1 rounded mr-1">A</span>
          )}
          {template.hidden_from_suggestions && (
            <span
              className="text-h4 text-neutral-500 bg-neutral-100 px-1 rounded mr-1"
              title="Masqué des suggestions dans l'envoi d'emails"
            >
              Masqué
            </span>
          )}
          {template.name}
        </div>
        <div className="text-neutral-400 text-h3">
          {getLocalFromFlutter(template.created_at)}
        </div>
      </div>
      <div className="text-h2 font-medium text-neutral-400 mr-auto">
        {template.subject.length > 60
          ? template.subject.substring(0, 60) + "..."
          : template.subject}
      </div>
      {docCount > 0 && (
        <div className="flex flex-row items-center gap-1 mr-3 text-h3 text-neutral-400">
          <Icon1 name="file" size={14} color="grey" />
          {docCount}
        </div>
      )}
      <SecondaryButton
        label="Modifier"
        variant="small"
        onClick={onSelect}
      />
    </div>
  );
};

export const EmailTemplates = () => {
  const emailTemplates = useProjectStore((state) => state.emailTemplates);
  const removeEmailTemplates = useProjectStore(
    (state) => state.removeEmailTemplates,
  );
  const reorderEmailTemplates = useProjectStore(
    (state) => state.reorderEmailTemplates,
  );
  const userData = useDataStore((state) => state.userData);
  const [value, setValue] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [newTemplateOpen, setNewTemplateOpen] = useState(false);
  const [isCreatingDefaults, setIsCreatingDefaults] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [localOrder, setLocalOrder] = useState<EmailTemplate[] | null>(null);

  const handleCreateDefaults = async () => {
    if (!userData?.org_id) {
      showNotification({
        type: "error",
        message:
          "Données utilisateur non disponibles. Veuillez recharger la page.",
      });
      return;
    }

    setIsCreatingDefaults(true);
    try {
      const results = await Promise.all(
        defaultEmailTemplates.map((template) =>
          emailTemplatesApi.create({
            ...template,
            org_id: userData.org_id,
          }),
        ),
      );
      useProjectStore
        .getState()
        .setEmailTemplates([
          ...useProjectStore.getState().emailTemplates,
          ...results,
        ]);
      showNotification({
        type: "success",
        message: `${results.length} modèles par défaut ajoutés`,
      });
    } catch (error: any) {
      console.error("Error creating default templates:", error);
      const detail = error?.response?.data?.detail;
      showNotification({
        type: "error",
        message: detail
          ? `Erreur : ${detail}`
          : "Erreur lors de la création des modèles",
      });
    } finally {
      setIsCreatingDefaults(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleDeleteSelected = () => {
    removeEmailTemplates(selectedIds);
    showNotification({
      type: "success",
      message: `${selectedIds.length} modèle(s) supprimé(s)`,
    });
    setSelectedIds([]);
    setShowDeleteConfirm(false);
  };

  const isSearching = value.trim().length > 0;

  const filteredTemplates = emailTemplates.filter((model) =>
    (
      model.name.toLowerCase() +
      model.subject.toLocaleLowerCase() +
      model.body.toLocaleLowerCase()
    ).includes(value.toLowerCase()),
  );

  const displayTemplates = isSearching
    ? filteredTemplates
    : localOrder ?? emailTemplates;

  const moveRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const current = localOrder ?? [...emailTemplates];
      const updated = [...current];
      const [removed] = updated.splice(dragIndex, 1);
      updated.splice(hoverIndex, 0, removed);
      setLocalOrder(updated);
    },
    [localOrder, emailTemplates],
  );

  const handleDropEnd = useCallback(() => {
    if (localOrder) {
      reorderEmailTemplates(localOrder);
      setLocalOrder(null);
    }
  }, [localOrder, reorderEmailTemplates]);

  return (
    <div className="flex flex-col h-fit pb-96 mt-10 mx-4 lg:ml-10">
      {(selectedTemplate !== null || newTemplateOpen) && (
        <EmailTemplateModal
          templateId={selectedTemplate || ""}
          onClose={() => {
            setSelectedTemplate(null);
            setNewTemplateOpen(false);
          }}
        />
      )}
      {showDeleteConfirm && (
        <ConfirmationModal
          title={`Supprimer ${selectedIds.length} modèle(s) ?`}
          message="Cette action est irréversible. Les modèles sélectionnés seront supprimés définitivement."
          confirmText="CONFIRMER"
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteSelected}
        />
      )}
      <div className="flex flex-col w-full max-w-400 gap-4">
        <div className="flex flex-col">
          <Text1>Modèles d'emails</Text1>
          <Text2>Configurez vos modèles d'emails ici.</Text2>
          <Text3>
            Gérez les modèles d'emails utilisés pour les notifications et les
            communications automatisées.
          </Text3>
        </div>
      </div>
      <div className="flex flex-col gap-2 mt-4 bg-white max-w-900 p-4 rounded-lg border-1">
        <div className="flex flex-row gap-2 items-center">
          <div className="w-full max-w-400">
            <SearchField
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="paiement, annulation, relance..."
            />
          </div>
          {selectedIds.length > 0 && (
            <SecondaryButton
              danger={true}
              startIcon={
                <Icon1 name="delete" size={22} color={colors.red_500} />
              }
              label={`Supprimer la sélection (${selectedIds.length})`}
              variant="large"
              onClick={() => setShowDeleteConfirm(true)}
            />
          )}
          {emailTemplates.length === 0 && (
            <SecondaryButton
              variant="small"
              startIcon={<Icon1 name="plus" size={14} />}
              onClick={handleCreateDefaults}
              disabled={isCreatingDefaults}
            >
              {isCreatingDefaults
                ? "Création en cours..."
                : "Ajouter les modèles par défaut"}
            </SecondaryButton>
          )}
        </div>

        <DndProvider backend={HTML5Backend}>
          <div className="flex flex-col divide-y mt-4">
            {displayTemplates.map((model, index) => (
              <DraggableTemplateRow
                key={model.id}
                template={model}
                index={index}
                isSelected={selectedIds.includes(model.id)}
                onToggleSelect={() => toggleSelect(model.id)}
                onSelect={() => setSelectedTemplate(model.id)}
                moveRow={moveRow}
                onDropEnd={handleDropEnd}
              />
            ))}
          </div>
        </DndProvider>

        <Button
          className="mt-4"
          label="Nouveau modèle d'email"
          onClick={() => setNewTemplateOpen(true)}
        />
      </div>
    </div>
  );
};
